// UpdateTodoWithComment.tsx
import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  IconButton,
  Input,
  Text,
  useDisclosure,
  VStack,
  HStack,
  Box,
  Flex,
  Icon,
  Divider,
  Textarea,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { CheckCircle2, MessageSquare, X } from "lucide-react";

interface Props {
  handleUpdate: (id: number, completedComment: string) => void;
  id: number;
}

interface Loading {
  loading: boolean;
}

interface form {
  completedComment: string;
}

const UpdateTodoWithComment: React.FC<Props> = ({ handleUpdate, id }) => {
  const {
    register: register2,
    handleSubmit: handleSubmit2,
    formState: { errors },
    reset,
  } = useForm<form>();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { loading } = useSelector((state: { common: Loading }) => state.common);

  const onsubmit = (data: any) => {
    handleUpdate(id, data.completedComment);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <IconButton
        size="sm"
        icon={<CheckCircle2 size={16} />}
        colorScheme="green"
        variant="ghost"
        onClick={onOpen}
        aria-label="Mark as completed"
      />

      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="2xl"
          mx={4}
          border="1px solid"
          borderColor="green.100"
        >
          {/* Header */}
          <ModalHeader p={0}>
            <Flex align="center" justify="space-between" px={6} pt={5} pb={4}>
              <Flex align="center" gap={3}>
                <Box
                  bg="green.500"
                  borderRadius="full"
                  p={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="0 4px 14px 0 rgba(34, 197, 94, 0.4)"
                >
                  <Icon as={CheckCircle2} w={5} h={5} color="white" />
                </Box>
                <Box>
                  <Text
                    fontSize="lg"
                    fontWeight="700"
                    color="gray.800"
                    letterSpacing="tight"
                  >
                    Complete Task
                  </Text>
                  <Text fontSize="xs" color="gray.500" fontWeight="500">
                    Add a completion comment
                  </Text>
                </Box>
              </Flex>

              <Box
                as="button"
                onClick={handleClose}
                p={1.5}
                borderRadius="lg"
                transition="all 0.2s"
                _hover={{ bg: "gray.100" }}
                _active={{ bg: "gray.200" }}
              >
                <Icon as={X} w={5} h={5} color="gray.400" />
              </Box>
            </Flex>
          </ModalHeader>

          {/* Body */}
          <ModalBody px={6} py={4}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600" fontWeight="500">
                Please add a comment about this task completion.
              </Text>

              <FormControl isInvalid={!!errors?.completedComment}>
                <HStack spacing={2} mb={2}>
                  <MessageSquare size={16} color="#6B7280" />
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    Completion Comment *
                  </Text>
                </HStack>
                <Textarea
                  {...register2("completedComment", {
                    required: "Comment is required.",
                  })}
                  placeholder="Add your completion comment..."
                  borderRadius="lg"
                  borderColor={
                    errors?.completedComment ? "red.500" : "gray.300"
                  }
                  minH="100px"
                  resize="vertical"
                  _focus={{
                    borderColor: "green.500",
                    boxShadow: "0 0 0 1px #22c55e",
                  }}
                />

                {errors?.completedComment && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    Comment is required.
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          {/* Footer */}
          <ModalFooter
            px={6}
            py={4}
            bg="gray.50"
            borderTop="1px solid"
            borderColor="gray.100"
          >
            <HStack spacing={3} w="full">
              <Button
                flex={1}
                variant="ghost"
                size="md"
                onClick={handleClose}
                fontSize="sm"
                fontWeight="600"
                h="44px"
                borderRadius="xl"
                color="gray.700"
                _hover={{
                  bg: "gray.200",
                }}
                _active={{
                  bg: "gray.300",
                }}
              >
                Cancel
              </Button>
              <Button
                flex={1}
                size="md"
                onClick={handleSubmit2(onsubmit)}
                isLoading={loading}
                loadingText="Completing..."
                fontSize="sm"
                fontWeight="700"
                h="44px"
                borderRadius="xl"
                bg="green.500"
                color="white"
                leftIcon={<Icon as={CheckCircle2} w={4} h={4} />}
                _hover={{
                  bg: "green.600",
                  transform: "translateY(-1px)",
                  boxShadow: "0 8px 16px -4px rgba(34, 197, 94, 0.4)",
                }}
                _active={{
                  transform: "translateY(0)",
                  bg: "green.700",
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                boxShadow="0 4px 12px -2px rgba(34, 197, 94, 0.3)"
              >
                Mark as Completed
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateTodoWithComment;
